import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { LookupWorkshop } from '../../../../../_shared/models/lookup.model';
import { LookupService } from '../../../../../_shared/services/lookup.service';
import { ComplianceFrequency } from '../../../../compliance-library/models/compliances.model';
import { EditPeriodicComplianceTaskRequest, PeriodicComplianceTask } from '../../../models/compliance-item.model';
import { ComplianceOverviewService } from '../../../services/compliance-overview.service';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LookupLabelPipe } from '../../../../../../_shared/pipes/lookup-label.pipe';
import { translateComplianceFrequency } from '../../../../_shared/helpers/compliance-lookup-label.helper';

@Component({
  selector: 'app-edit-periodic-compliance-task-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbDropdownModule,
    TranslocoDirective,
    TranslocoPipe,
    LookupLabelPipe
  ],
  templateUrl: './edit-periodic-compliance-task-modal.component.html',
  styleUrl: './edit-periodic-compliance-task-modal.component.scss'
})
export class EditPeriodicComplianceTaskModalComponent implements OnDestroy {
  private activeModal = inject(NgbActiveModal);
  private fb = inject(FormBuilder);
  private lookupService = inject(LookupService);
  private complianceOverviewService = inject(ComplianceOverviewService);
  private errorHandlerService = inject(ErrorHandlerService);
  private toastr = inject(ToastrService);
  private transloco = inject(TranslocoService);

  private cancellation = new Subject<void>();
  private workshopCancellation = new Subject<void>();

  private initialFormValues: { workshopId: number | null; frequencyId: number | null } | null = null;
  private _task!: PeriodicComplianceTask;

  @Input()
  set task(value: PeriodicComplianceTask) {
    this._task = value;
    if (value) {
      this.populateReadonlyFields();
      void this.loadLookupsAsync();
    }
  }

  get task(): PeriodicComplianceTask {
    return this._task;
  }

  @Input() accountId: number = 0;

  editForm: FormGroup = this.fb.group({
    assetName: [{ value: '', disabled: true }],
    complianceStandard: [{ value: '', disabled: true }],
    workshopId: [null as number | null],
    frequencyId: [null as number | null, Validators.required]
  });

  workshops: LookupWorkshop[] = [];
  frequencies: ComplianceFrequency[] = [];
  selectedWorkshopName = '';
  selectedFrequencyName = '';
  isLoading = false;
  isLoadingWorkshops = false;
  isLoadingFrequencies = false;
  hasChanges = false;

  ngOnDestroy(): void {
    this.cancellation.next();
    this.cancellation.complete();
    this.workshopCancellation.next();
    this.workshopCancellation.complete();
  }

  get workshopLabel(): string {
    return this.selectedWorkshopName || this.transloco.translate('compliance.overview.editPeriodicModal.selectWorkshop');
  }

  get frequencyLabel(): string {
    return this.selectedFrequencyName
      ? translateComplianceFrequency(this.transloco, this.selectedFrequencyName)
      : this.transloco.translate('compliance.overview.editPeriodicModal.selectFrequency');
  }

  closeModal(): void {
    this.activeModal.dismiss();
  }

  onCancel(): void {
    this.activeModal.dismiss();
  }

  onWorkshopSelect(workshop: LookupWorkshop, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.selectedWorkshopName = workshop.name;
    this.editForm.get('workshopId')?.setValue(workshop.id);
    this.editForm.get('workshopId')?.markAsTouched();
    this.checkForChanges();
  }

  onFrequencySelect(frequency: ComplianceFrequency, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.selectedFrequencyName = frequency.name;
    this.editForm.get('frequencyId')?.setValue(frequency.id);
    this.editForm.get('frequencyId')?.markAsTouched();
    this.checkForChanges();
  }

  async onSubmit(): Promise<void> {
    if (this.editForm.invalid || !this.hasChanges || this.isLoading) {
      this.editForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;

    try {
      const raw = this.editForm.getRawValue();
      const request: EditPeriodicComplianceTaskRequest = {
        id: this.task.id,
        assetId: this.task.assetId,
        workshopId: raw.workshopId,
        frequencyId: raw.frequencyId
      };

      await this.complianceOverviewService.editPeriodicComplianceTaskAsync(request);
      this.toastr.success(this.transloco.translate('compliance.overview.editPeriodicModal.toasts.updated'));
      this.isLoading = false;
      this.activeModal.close(true);
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  private populateReadonlyFields(): void {
    this.editForm.patchValue({
      assetName: this.task.assetName,
      complianceStandard: this.task.complianceStandard
    });
  }

  private async loadLookupsAsync(): Promise<void> {
    await Promise.all([
      this.loadWorkshopsAsync(),
      this.loadFrequenciesAsync()
    ]);

    this.resolveInitialSelections();
    this.storeInitialValues();
  }

  private async loadWorkshopsAsync(): Promise<void> {
    this.isLoadingWorkshops = true;

    try {
      this.workshopCancellation.next();

      const response = await this.lookupService.getWorkshopsByAccountHierarchyAsync(this.accountId, this.workshopCancellation);
      this.workshops = response.data ?? [];
      this.isLoadingWorkshops = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoadingWorkshops = false;
        }
      });
    }
  }

  private async loadFrequenciesAsync(): Promise<void> {
    this.isLoadingFrequencies = true;

    try {
      const response = await this.lookupService.getComplianceFrequenciesAsync();
      this.frequencies = response.data ?? [];
      this.isLoadingFrequencies = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoadingFrequencies = false;
        }
      });
    }
  }

  private resolveInitialSelections(): void {
    const workshop = this.workshops.find(w => this.normalize(w.name) === this.normalize(this.task.workshopName));
    const frequency = this.frequencies.find(f => this.normalize(f.name) === this.normalize(this.task.frequency));

    if (workshop) {
      this.selectedWorkshopName = workshop.name;
      this.editForm.get('workshopId')?.setValue(workshop.id);
    }

    if (frequency) {
      this.selectedFrequencyName = frequency.name;
      this.editForm.get('frequencyId')?.setValue(frequency.id);
    }
  }

  private storeInitialValues(): void {
    const raw = this.editForm.getRawValue();
    this.initialFormValues = {
      workshopId: raw.workshopId,
      frequencyId: raw.frequencyId
    };
    this.hasChanges = false;
  }

  private checkForChanges(): void {
    if (!this.initialFormValues) {
      return;
    }

    const raw = this.editForm.getRawValue();
    this.hasChanges =
      raw.workshopId !== this.initialFormValues.workshopId ||
      raw.frequencyId !== this.initialFormValues.frequencyId;
  }

  private normalize(value?: string | null): string {
    return (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
  }
}